import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { API_URL_BASE } from '@/config';
import preloadChunks from '@/routes/preloadChunks.util';
import { maskEmail } from '@/utils/maskString';
import useSign from '@/pages/SignLayout.context';
import useApi from '@/hooks/useApi';
import triggerDownloadFlow from '@/utils/downloadFile';
import Button from '@/components/Button';
import FormPassword from '@/components/FormPassword';
import FormError from '@/components/FormError';
import styles from './styles.module.scss';

interface Props {
  viewLabel?: string;
  downloadLabel?: string;
}

type LoginInputs = {
  password: string;
};

const useViewAndDownload = (
  {
    viewLabel = 'sign:download.view',
    downloadLabel = 'sign:download.download',
  }: Props = {
    viewLabel: 'sign:download.view',
    downloadLabel: 'sign:download.download',
  }
) => {
  const { t } = useTranslation(['sign']);
  const {
    loginInfo,
    setLoginSuccessful,
    setLoginTimestamp,
    signing,
    signers,
    visitor,
  } = useSign();
  const navigate = useNavigate();
  const [login, { loading }] = useApi('users.login', {
    omitRedirectToLogin: true,
  });

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginInputs>();

  const doLogin: SubmitHandler<LoginInputs> = async (data) => {
    const values = {
      email: visitor?.email ?? '',
      password: data.password,
    };

    const result = await login(values);

    if (!result) {
      return;
    }

    if (result.error?.statusCode === 401) {
      setError('password', { message: t('sign:fields.password.invalid') });
      return;
    }

    const token = result.response?.token ?? 'not-a-valid-token';
    localStorage.setItem('token', token);
    sessionStorage.setItem('token', token);

    setLoginSuccessful();
    setLoginTimestamp(new Date());
  };

  const handlePreload = () => {
    void preloadChunks['sign.document']();
  };

  const handleNextStep = () => {
    if (signing && visitor) {
      void navigate(`/sign/${signing.id}/${visitor.id}/document`, {
        replace: true,
      });
    }
  };

  const handleDownload = () => {
    if (!signing) {
      return;
    }

    const url =
      signing.documents.length > 1
        ? `${API_URL_BASE}signings/${signing.id}/sign/documents/download-all`
        : signing.documents[0]
          ? `${API_URL_BASE}signings/${signing.id}/sign/documents/${signing.documents[0].id}`
          : undefined;

    if (url) {
      void triggerDownloadFlow(url, sessionStorage.getItem('token'), {
        loading: t('sign:download.downloading'),
        failed: t('sign:download.failed'),
        success: t('sign:download.success'),
      });
    }
  };

  if (!signing || !signers || !visitor) {
    return { view: null, download: null, login: null, all: null };
  }

  const toReturn = {
    view: (
      <Button
        className={styles.button}
        variant="primary"
        onClick={handleNextStep}
        onMouseEnter={handlePreload}
        onFocus={handlePreload}
      >
        {t(viewLabel)}
      </Button>
    ),
    download: (
      <Button
        className={styles.button}
        variant="tertiary"
        onClick={handleDownload}
      >
        {t(downloadLabel)}
      </Button>
    ),
    login: (
      <form onSubmit={handleSubmit(doLogin)} className={styles.form}>
        <div className={styles.into}>
          {t('sign:login.intro', { email: maskEmail(visitor.email) })}
        </div>
        <FormPassword
          autoComplete="current-password"
          className={styles.passwordWrapper}
          errorMessage={errors.password?.message}
          id="password"
          label={t('sign:fields.password')}
          placeholder={t('sign:fields.passwordPlaceholder')}
          {...register('password')}
        />
        {errors.root?.message && (
          <FormError isRoot message={errors.root.message} />
        )}
        <Button className={styles.button} submit loading={loading}>
          {t('sign:login.submit')}
        </Button>
      </form>
    ),
    all: <div />,
  };

  if (loginInfo.needed && !loginInfo.valid()) {
    toReturn.all = <div className={styles.start}>{toReturn.login}</div>;
  } else {
    toReturn.all = (
      <div className={styles.start}>
        {toReturn.view}
        {toReturn.download}
      </div>
    );
  }

  return toReturn;
};

export default useViewAndDownload;
